import React, { useState, useEffect, useMemo } from 'react';
import { css } from '@emotion/react';
import { Button } from 'react-bootstrap';
import { connect } from 'react-redux';
import { fetchWrapper } from '../../../../_helpers';
import { PeopleItem, StyledPeople } from './styles/PeopleStyles';
import { toast } from 'react-toastify';
import OurAccordion from '@/_elements/OurAccordion';

function People({
    currUser,
    roomId,
    channelMembers,
    teamMembers,
    rooms,
    conferenceId,
    participants,
    twilioParticipants,
    allParticipantsInfo,
    waitingParticipants,
    channelInfo,
    handleMuteParticipant,
    isHost,
    conferenceHostId,
    handleRemoveParticipant,
}) {
    const [invitableMembers, setInvitableMembers] = useState([]);

    const [inCallMembers, setInCallMembers] = useState([]);
    const localParticipant = useMemo(() => {
        if (!currUser) return {};
        return {
            ...currUser,
            local: true,
            isMeetingHost: (currUser?._id || currUser?.id) === conferenceHostId,
        };
    }, [conferenceHostId, currUser]);

    useEffect(() => {
        const twilioParticipantIds = new Set();
        if (twilioParticipants && Array.isArray(twilioParticipants)) {
            twilioParticipants.forEach((participant) => {
                const identity = participant?.identity;
                if (identity) {
                    twilioParticipantIds.add(identity.toString());
                }
            });
        }
        const currentUserId = currUser?.id || currUser?._id;
        const finalParticipantIds = new Set(twilioParticipantIds);
        if (currentUserId) {
            finalParticipantIds.delete(currentUserId);
        }

        // Build participant objects by merging data from different sources
        const callParticipantArray = Array.from(finalParticipantIds)
            .map((id) => {
                let participant = null;
                if (allParticipantsInfo && allParticipantsInfo[id]) {
                    participant = allParticipantsInfo[id];
                } else if (channelMembers && Array.isArray(channelMembers)) {
                    participant = channelMembers.find((member) => (member.id || member._id)?.toString() === id);
                } else if (teamMembers && Array.isArray(teamMembers)) {
                    participant = teamMembers.find((member) => (member.id || member._id)?.toString() === id);
                } else if (participants && participants[id]) {
                    participant = participants[id];
                }

                // If still no data, create a basic participant object for current user
                if (!participant) {
                    participant = {
                        _id: id,
                        id: id,
                        name: 'Unknown User',
                        email: '',
                    };
                }

                const participantId = (participant.id || participant._id)?.toString();
                return {
                    ...participant,
                    local: participantId === currentUserId,
                    isMeetingHost: participantId === (conferenceHostId || ''),
                };
            })
            .filter((p) => p && p.name && (p.id || p._id)?.toString() !== currentUserId)
            .sort((a, b) => {
                if (a.local && !b.local) return -1;
                if (!a.local && b.local) return 1;
                return a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1;
            });

        setInCallMembers(callParticipantArray);

        // Build list of invitable members (those not in the call)
        const callParticipantIDArray = callParticipantArray.map((p) => (p.id || p._id)?.toString());

        // Also include current user ID to exclude them from invitable list
        const excludeIds = [...callParticipantIDArray, currentUserId].filter(Boolean);

        // Determine which members list to use based on channel type
        const members = channelInfo?.type === 'public' ? teamMembers : channelMembers;

        if (members && Array.isArray(members)) {
            const invitable = members.filter((member) => {
                const memberId = (member.id || member._id)?.toString();
                return memberId && !excludeIds.includes(memberId);
            });
            setInvitableMembers(invitable);
        } else {
            setInvitableMembers([]);
        }
    }, [
        twilioParticipants,
        allParticipantsInfo,
        channelMembers,
        teamMembers,
        currUser.id,
        currUser,
        conferenceHostId,
        channelInfo?.type,
        participants,
    ]);

    const handleInvite = async (channelMember) => {
        let userId = channelMember.id || channelMember._id;
        try {
            await fetchWrapper.post('conference/invite', {
                conferenceId: conferenceId,
                userIds: [userId],
            });
            toast.info('Invitation sent successfully!');
        } catch (e) {
            console.error('Failed to send invitation:', e);
            toast.error('Failed to send invitation', e?.message);
        }
    };
    const handleInviteAll = async () => {
        try {
            let invitableMembersId = invitableMembers.map((member) => member._id || member.id);
            await fetchWrapper.post('conference/invite', {
                conferenceId: conferenceId,
                userIds: invitableMembersId,
            });
            toast.info('Invitation sent successfully!');
        } catch (e) {
            console.error('Failed to send invitations:', e);
            toast.error('Failed to send invitations', e?.message);
        }
    };

    const removeParticipant = (participantId) => {
        let tempInCallMembers = [...inCallMembers];
        let removedParticipant;
        tempInCallMembers = tempInCallMembers
            .filter((participant) => {
                if ((participant.id || participant._id) === participantId) {
                    removedParticipant = participant;
                    return false;
                }
                return true;
            })
            .filter((x) => x);
        setInCallMembers(tempInCallMembers);
        setInvitableMembers([...invitableMembers, removedParticipant]);
        handleRemoveParticipant(participantId);
    };
    return (
        <StyledPeople>
            <div>
                {isHost && waitingParticipants && waitingParticipants.length > 0 && (
                    <OurAccordion
                        title={
                            <div>
                                <span>Waiting To Be Admitted</span>
                                <span className="badge badge-warning ml-2">{waitingParticipants.length}</span>
                            </div>
                        }
                    >
                        <div>
                            <div
                                css={css`
                                    max-height: 55vh;
                                    overflow-y: auto;
                                    padding: 0 1rem;
                                `}
                            >
                                <div className="d-flex justify-content-end">
                                    <Button
                                        onClick={() => {
                                            try {
                                                fetchWrapper.patch(`/conference/${conferenceId}/settings`, {
                                                    accessType: 'open',
                                                });
                                            } catch (error) {
                                                console.error(error);
                                                toast.error('Failed to update settings');
                                            }
                                        }}
                                        variant="secondary"
                                        style={{ padding: '0.2rem 0.5rem', borderRadius: '6px' }}
                                    >
                                        Admit All
                                    </Button>
                                </div>

                                {waitingParticipants?.map((participant) => {
                                    return (
                                        <>
                                            <PeopleItem
                                                key={participant.id || participant._id}
                                                people={participant}
                                                isHost={isHost}
                                                handleRemoveParticipant={removeParticipant}
                                                handleMuteParticipant={handleMuteParticipant}
                                                isWaiting={true}
                                            />
                                        </>
                                    );
                                })}
                            </div>
                        </div>
                    </OurAccordion>
                )}

                <OurAccordion title={'In Call'}>
                    <div>
                        <div
                            css={css`
                                max-height: 55vh;
                                overflow-y: auto;
                                padding: 0 1rem;
                            `}
                            key={participants}
                        >
                            {localParticipant?.id && (
                                <PeopleItem
                                    key={localParticipant.id}
                                    people={localParticipant}
                                    isHost={isHost}
                                    handleRemoveParticipant={removeParticipant}
                                    handleMuteParticipant={handleMuteParticipant}
                                />
                            )}
                            {inCallMembers?.map((participant) => {
                                return (
                                    <>
                                        <PeopleItem
                                            key={participant.id}
                                            people={participant}
                                            isHost={isHost}
                                            handleRemoveParticipant={removeParticipant}
                                            handleMuteParticipant={handleMuteParticipant}
                                        />
                                    </>
                                );
                            })}
                        </div>
                    </div>
                </OurAccordion>
                <OurAccordion title={'Invite'} defaultShow={false}>
                    <div>
                        <div
                            css={css`
                                max-height: 55vh;
                                overflow-y: auto;
                                padding: 0 1rem;
                            `}
                        >
                            {invitableMembers?.length ? (
                                <div
                                    css={css`
                                        display: flex;
                                        justify-content: flex-end;
                                        margin-top: 1rem;
                                    `}
                                >
                                    <Button className="BlueButton" onClick={handleInviteAll}>
                                        Invite all
                                    </Button>
                                </div>
                            ) : null}

                            {invitableMembers
                                .sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1))
                                .map((channelMember) => (
                                    <PeopleItem
                                        key={channelMember.id || channelMember._id}
                                        people={channelMember}
                                        handleInvite={handleInvite}
                                    />
                                ))}
                        </div>
                    </div>
                </OurAccordion>
            </div>
        </StyledPeople>
    );
}
const mapStateToProps = (state) => ({
    currUser: state.user.user,
    rooms: state.rooms.teamRooms,
    teamMembers: state.teams.teamMembers,
    channelMembers: state.rooms.channelMembers,
});

export default connect(mapStateToProps)(People);
